import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import type { DmAutomationDto } from '@/types/dmAutomation';
import type { DmSection } from '@/layouts/AutoDmLayout/Sidebar';
import AutomationListPage from './AutomationListPage';
import SendLogsPage from './SendLogsPage';
import InstagramAccountPage from './InstagramAccountPage';
import SubscriptionPage from './SubscriptionPage';
import HelpPage from './HelpPage';
import AutomationFormPage from './AutomationFormPage';
import PreviewPanel from './PreviewPanel';

export type AutoDmView = 'list' | 'wizard' | 'detail';

interface Props {
    section: DmSection;
    view: AutoDmView;
    editing: DmAutomationDto | null;
    onChangeView: (view: AutoDmView) => void;
    onChangeSection: (section: DmSection) => void;
    onStartEdit: (automation: DmAutomationDto) => void;
    onExitWizard: () => void;
}

const AutoDmRoot = ({
    section,
    view,
    editing,
    onChangeView,
    onChangeSection,
    onStartEdit,
    onExitWizard,
}: Props) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // 다른 섹션으로 이동 시 선택 해제
    useEffect(() => {
        if (section !== 'list' || view !== 'list') {
            setSelectedId(null);
        }
    }, [section, view]);

    const showPreview = section === 'list' && view === 'list';

    const renderMain = () => {
        if (section === 'list' && view === 'wizard') {
            return (
                <AutomationFormPage
                    key={editing?.id ?? 'new'}
                    automation={editing ?? undefined}
                    onExit={onExitWizard}
                />
            );
        }
        if (section === 'list') {
            return (
                <AutomationListPage
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onStartWizard={() => onChangeView('wizard')}
                    onGoToAccount={() => onChangeSection('account')}
                    onEdit={onStartEdit}
                />
            );
        }
        if (section === 'logs') return <SendLogsPage />;
        if (section === 'account') return <InstagramAccountPage />;
        if (section === 'subscription') return <SubscriptionPage />;
        if (section === 'help') return <HelpPage />;
        return null;
    };

    return (
        <>
            <Box
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    overflowY: 'auto',
                    px: { xs: 3, md: 4 },
                    py: 3.5,
                }}
            >
                {renderMain()}
            </Box>

            {showPreview && <PreviewPanel selectedId={selectedId} />}
        </>
    );
};

export default AutoDmRoot;
